import type { CampaignSendStats } from '../types/campaign-send-stats';

const computedAt = new Date().toISOString();

function daysBack(n: number, hour = 14, minute = 13): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

function doc(
  partial: Omit<CampaignSendStats, 'ComputedAt' | 'IsDeleted'> & {
    ComputedAt?: string;
    IsDeleted?: boolean;
  }
): CampaignSendStats {
  return {
    ComputedAt: computedAt,
    IsDeleted: false,
    ...partial,
  };
}

/**
 * Blocks-shaped seed for CampaignSendStats.
 * Numbers align with the design prototype (epic #44). Share this shape with BE.
 */
export const MOCK_CAMPAIGN_SEND_STATS: CampaignSendStats[] = [
  doc({
    ItemId: 'css-pricing',
    CampaignId: 'cmp-pricing',
    Name: 'Pricing change notice',
    Status: 'sent',
    SentAt: daysBack(26),
    Audience: 820,
    Sent: 780,
    Delivered: 764,
    Bounced: 16,
    BouncedHard: 9,
    BouncedSoft: 7,
    UniqueOpens: 198,
    UniqueClicks: 48,
    Complaints: 0,
    Unsubscribes: 3,
    SkipTotal: 40,
  }),
  doc({
    ItemId: 'css-q3',
    CampaignId: 'cmp-q3',
    Name: 'Q3 roundup',
    Status: 'sent',
    SentAt: daysBack(18),
    Audience: 910,
    Sent: 880,
    Delivered: 862,
    Bounced: 18,
    BouncedHard: 10,
    BouncedSoft: 8,
    UniqueOpens: 241,
    UniqueClicks: 55,
    Complaints: 1,
    Unsubscribes: 4,
    SkipTotal: 30,
  }),
  doc({
    ItemId: 'css-reeng',
    CampaignId: 'cmp-reeng',
    Name: 'Re-engagement: dormant 90d',
    Status: 'sent',
    SentAt: daysBack(12),
    Audience: 640,
    Sent: 610,
    Delivered: 592,
    Bounced: 18,
    BouncedHard: 11,
    BouncedSoft: 7,
    UniqueOpens: 148,
    UniqueClicks: 34,
    Complaints: 2,
    Unsubscribes: 6,
    SkipTotal: 30,
  }),
  doc({
    ItemId: 'css-autumn',
    CampaignId: 'cmp-autumn',
    Name: 'Autumn product launch',
    Status: 'sent',
    SentAt: daysBack(7),
    Audience: 980,
    Sent: 940,
    Delivered: 922,
    Bounced: 18,
    BouncedHard: 10,
    BouncedSoft: 8,
    UniqueOpens: 248,
    UniqueClicks: 62,
    Complaints: 0,
    Unsubscribes: 5,
    SkipTotal: 40,
  }),
  doc({
    ItemId: 'css-digest',
    CampaignId: 'cmp-digest',
    Name: 'Weekly digest',
    Status: 'sent',
    SentAt: daysBack(2),
    Audience: 720,
    Sent: 649,
    Delivered: 641,
    Bounced: 8,
    BouncedHard: 4,
    BouncedSoft: 4,
    UniqueOpens: 156,
    UniqueClicks: 42,
    Complaints: 0,
    Unsubscribes: 2,
    SkipTotal: 71,
  }),
  doc({
    ItemId: 'css-failed',
    CampaignId: 'cmp-failed-demo',
    Name: 'Flash sale (failed send)',
    Status: 'failed',
    SentAt: daysBack(4, 9, 5),
    Audience: 400,
    Sent: 120,
    Delivered: 0,
    Bounced: 0,
    BouncedHard: 0,
    BouncedSoft: 0,
    UniqueOpens: 0,
    UniqueClicks: 0,
    Complaints: 0,
    Unsubscribes: 0,
    SkipTotal: 0,
  }),
  doc({
    ItemId: 'css-sending',
    CampaignId: 'cmp-sending-demo',
    Name: 'Partner newsletter (in flight)',
    Status: 'sending',
    SentAt: daysBack(0, 11, 20),
    Audience: 500,
    Sent: 310,
    Delivered: 298,
    Bounced: 4,
    BouncedHard: 2,
    BouncedSoft: 2,
    UniqueOpens: 41,
    UniqueClicks: 9,
    Complaints: 0,
    Unsubscribes: 1,
    SkipTotal: 12,
  }),
];

/** Simulated GraphQL response for the default seed (no filter). */
export function mockCampaignSendStatsListResponse(items = MOCK_CAMPAIGN_SEND_STATS) {
  return {
    CampaignSendStatss: {
      items,
      totalCount: items.length,
      pageNo: 1,
      pageSize: items.length,
      hasNextPage: false,
      hasPreviousPage: false,
    },
  };
}
